import { createContext, useContext, useState, useCallback } from 'react';

// Create a context for the request queue
const RequestQueueContext = createContext();

/**
 * Hook to access the request queue context
 * Use this in components to track API requests
 */
export const useRequestQueue = () => {
  const context = useContext(RequestQueueContext);
  if (!context) {
    throw new Error('useRequestQueue must be used within RequestQueueProvider');
  }
  return context;
};

/**
 * RequestQueueProvider - Provides request tracking functionality to all child components
 * 
 * This provider manages:
 * - A queue of all API requests (pending, success, failed)
 * - A list of errors that occurred
 * - Functions to add/update/remove requests
 * - Counts of pending and failed requests
 */
export const RequestQueueProvider = ({ children }) => {
  // State: Array of all requests with their status
  const [queue, setQueue] = useState([]);
  
  // State: Array of errors that need to be shown to the user
  const [errors, setErrors] = useState([]);

  /**
   * Add a new request to the queue
   * @param {Object} request - Request object with type, description, url, etc.
   * @returns {string} - Unique request ID
   */
  const addRequest = useCallback((request) => {
    const id = Date.now() + Math.random();
    const requestWithId = {
      id,
      status: 'pending', // pending, success, failed
      timestamp: new Date(),
      ...request,
    };
    
    setQueue(prev => [...prev, requestWithId]);
    
    return id;
  }, []);

  /**
   * Update an existing request (e.g., mark as success/failed)
   * @param {string} id - Request ID
   * @param {Object} updates - Updates to apply (status, data, etc.)
   */
  const updateRequest = useCallback((id, updates) => {
    setQueue(prev => prev.map(req => 
      req.id === id ? { ...req, ...updates } : req
    ));
  }, []);

  /**
   * Add an error to the error list (shown in UI)
   * @param {Object} error - Error object with message, type, url, status, etc.
   */
  const addError = useCallback((error) => {
    const errorWithId = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      ...error,
    };
    setErrors(prev => [...prev, errorWithId]);
  }, []);

  /**
   * Remove a request from the queue (after showing success for a few seconds)
   * @param {string} id - Request ID
   */
  const removeRequest = useCallback((id) => {
    setQueue(prev => prev.filter(req => req.id !== id));
  }, []);

  /**
   * Clear all errors from the error list
   */
  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // Calculate counts
  const pendingCount = queue.filter(r => r.status === 'pending').length;
  const failedCount = queue.filter(r => r.status === 'failed').length;
  const successCount = queue.filter(r => r.status === 'success').length;

  return (
    <RequestQueueContext.Provider value={{
      queue,
      errors,
      addRequest,
      updateRequest,
      removeRequest,
      addError,
      clearErrors,
      pendingCount,
      failedCount,
      successCount,
    }}>
      {children}
    </RequestQueueContext.Provider>
  );
};

